import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabaseClient";
import type { Item } from "../types/orderbook";
import { logger } from "../lib/logger";
import { notifyListCollaborators } from "../lib/notificationService";

// Fetch items for a specific list
async function fetchItemsForList(listId: string | null): Promise<Item[]> {
  const {
    data: { user }
  } = await supabase.auth.getUser();
  
  if (!user) {
    return [];
  }

  let query = supabase
    .from("items")
    .select("*")
    .order("created_at", { ascending: false });
  
  if (listId) {
    query = query.eq("list_id", listId);
  } else {
    // For "All items", only show items owned by the user (not collaborative)
    query = query.eq("user_id", user.id);
  }

  const { data, error } = await query;

  if (error) {
    logger.error("Error loading items", error);
    throw error;
  }

  return (data ?? []) as Item[];
}

// Hook to get items for a list
export function useItems(listId: string | null) {
  return useQuery({
    queryKey: ["items", listId],
    queryFn: () => fetchItemsForList(listId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus for items
  });
}

// Hook to create an item
export function useCreateItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      listId: string | null;
      name: string;
      url?: string;
      description?: string;
      price?: number;
      currency?: string;
      imageUrl?: string;
      merchantName?: string;
      brandName?: string;
    }) => {
      const {
        data: { user },
        error: userError
      } = await supabase.auth.getUser();
      
      if (userError || !user) {
        throw new Error("Not authenticated");
      }

      const { data, error } = await supabase
        .from("items")
        .insert({
          name: payload.name,
          url: payload.url ?? null,
          description: payload.description ?? null,
          price: payload.price ?? null,
          currency: payload.currency ?? "USD",
          image_url: payload.imageUrl ?? null,
          merchant_name: payload.merchantName ?? null,
          brand_name: payload.brandName ?? null,
          user_id: user.id,
          list_id: payload.listId
        })
        .select("*")
        .single();

      if (error) {
        logger.error("Error creating item", error);
        throw error;
      }

      logger.log("Item created successfully:", data.id);
      return data as Item;
    },
    onSuccess: async (data) => {
      // Invalidate and refetch items for the list
      queryClient.invalidateQueries({ queryKey: ["items", data.list_id] });
      // Also invalidate "all items" if this item has no list
      if (!data.list_id) {
        queryClient.invalidateQueries({ queryKey: ["items", null] });
      }
      // Invalidate lists to update item counts
      queryClient.invalidateQueries({ queryKey: ["lists"] });
      
      // Notify collaborators (don't await - fire and forget)
      if (data.list_id) {
        notifyListCollaborators({
          listId: data.list_id,
          actorId: data.user_id,
          type: "item_added",
          title: "New item added",
          message: `"${data.name}" was added to the list`,
          itemId: data.id,
        });
      }
    }
  });
}

// Hook to update an item
export function useUpdateItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      itemId: string;
      updates: {
        name?: string;
        description?: string | null;
        price?: number | null;
        currency?: string;
        list_id?: string | null;
      };
    }) => {
      const { data, error } = await supabase
        .from("items")
        .update(params.updates)
        .eq("id", params.itemId)
        .select("*")
        .single();

      if (error) {
        logger.error("Error updating item", error);
        throw error;
      }

      return data as Item;
    },
    onSuccess: (data, variables) => {
      // Invalidate items for both old and new list
      const oldItem = queryClient.getQueryData<Item[]>(["items", data.list_id]);
      if (oldItem) {
        const item = oldItem.find(i => i.id === data.id);
        if (item && item.list_id !== data.list_id) {
          // Item moved to different list
          queryClient.invalidateQueries({ queryKey: ["items", item.list_id] });
        }
      }
      queryClient.invalidateQueries({ queryKey: ["items", data.list_id] });
      queryClient.invalidateQueries({ queryKey: ["items", null] });
      queryClient.invalidateQueries({ queryKey: ["lists"] });
    }
  });
}

// Hook to delete an item
export function useDeleteItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (itemId: string) => {
      // Get the item first to know which list to invalidate
      const { data: item } = await supabase
        .from("items")
        .select("list_id")
        .eq("id", itemId)
        .single();

      const { error } = await supabase.from("items").delete().eq("id", itemId);

      if (error) {
        logger.error("Error deleting item", error);
        throw error;
      }

      logger.log("Item deleted successfully:", itemId);
      return { itemId, listId: item?.list_id ?? null };
    },
    onSuccess: (data) => {
      // Invalidate items for the list
      queryClient.invalidateQueries({ queryKey: ["items", data.listId] });
      queryClient.invalidateQueries({ queryKey: ["items", null] });
      queryClient.invalidateQueries({ queryKey: ["lists"] });
    }
  });
}

// Hook to toggle favorite
export function useToggleFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (itemId: string) => {
      // Get current item to check favorite status
      const { data: currentItem } = await supabase
        .from("items")
        .select("is_favorite, list_id")
        .eq("id", itemId)
        .single();

      if (!currentItem) {
        throw new Error("Item not found");
      }

      const newFavoriteStatus = !(currentItem.is_favorite ?? false);

      const { data, error } = await supabase
        .from("items")
        .update({ is_favorite: newFavoriteStatus })
        .eq("id", itemId)
        .select("*")
        .single();

      if (error) {
        logger.error("Error toggling favorite", error);
        throw error;
      }

      logger.log("Favorite toggled successfully:", itemId, newFavoriteStatus);
      return data as Item;
    },
    onSuccess: (data) => {
      // Optimistically update the cache
      queryClient.setQueryData<Item[]>(["items", data.list_id], (old) => {
        if (!old) return old;
        return old.map((item) => (item.id === data.id ? data : item));
      });
      queryClient.setQueryData<Item[]>(["items", null], (old) => {
        if (!old) return old;
        return old.map((item) => (item.id === data.id ? data : item));
      });
    }
  });
}
